import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { ResponsiveTable } from '../components/ResponsiveTable';
import { api } from '../utils/api';
import { useWebSocketContext } from '../context/WebSocketContext';
import { ArrowLeft, TrendingUp, BarChart3, Download, Plus, X } from 'lucide-react';
import { toast } from 'sonner';
import { saveAs } from 'file-saver';
import { handleNumericInputFocus, handleNumericInputMouseUp } from '../utils/inputHelpers';

export const EventDetail = () => {
  const { eventId } = useParams();
  const navigate = useNavigate();
  const [event, setEvent] = useState(null);
  const [summary, setSummary] = useState([]);
  const [totals, setTotals] = useState(null);
  const [movements, setMovements] = useState({ exits: [], entries: [] });
  const [entryDialogOpen, setEntryDialogOpen] = useState(false);
  const [entryFormData, setEntryFormData] = useState({
    barcode: '',
    product_id: '',
    product_name: '',
    quantity: ''
  });
  const [loading, setLoading] = useState(true);
  const [scanningProduct, setScanningProduct] = useState(false);
  
  // Modo lote para entradas
  const [batchMode, setBatchMode] = useState(false);
  const [batchItems, setBatchItems] = useState([]);
  const [confirmingBatch, setConfirmingBatch] = useState(false);
  
  // Editing event costs
  const [editingCosts, setEditingCosts] = useState(false);
  const [costFormData, setCostFormData] = useState({
    coste_camareros: 0,
    coste_gastos_variables: 0,
    coste_transporte: 0,
    coste_extra1: 0,
    coste_extra2: 0,
    income: 0
  });
  
  const { isConnected, subscribe } = useWebSocketContext();

  const fetchEventData = React.useCallback(async () => {
    try {
      setLoading(true);
      const [summaryRes, movementsRes] = await Promise.all([
        api.getEventSummary(eventId),
        api.getEventMovements(eventId)
      ]);
      
      setEvent(summaryRes.data.event);
      setSummary(summaryRes.data.summary);
      setTotals(summaryRes.data.totals);
      setMovements(movementsRes.data);
      
      // Initialize cost form data
      const ev = summaryRes.data.event;
      setCostFormData({
        coste_camareros: ev.coste_camareros || 0,
        coste_gastos_variables: ev.coste_gastos_variables || 0,
        coste_transporte: ev.coste_transporte || 0,
        coste_extra1: ev.coste_extra1 || 0,
        coste_extra2: ev.coste_extra2 || 0,
        income: ev.income || 0
      });
    } catch (error) {
      toast.error('Error al cargar datos del evento');
      console.error(error);
    } finally {
      setLoading(false);
    }
  }, [eventId]);

  // Carga inicial
  useEffect(() => {
    fetchEventData();
  }, [fetchEventData]);

  // Subscribe to WebSocket updates (instantáneo)
  useEffect(() => {
    const unsubscribe = subscribe((message) => {
      if (message.type === 'stock_update') {
        console.log('🔄 EventDetail: Actualización WebSocket recibida', message);
        fetchEventData();
        
        const productName = message.product?.name || 'Producto';
        const movementType = message.movement?.type === 'entry' ? 'Entrada' : 'Salida';
        toast.info(`${movementType}: ${productName}`, {
          description: 'Evento actualizado',
          duration: 2000
        });
      } else if (message.type === 'event_status_update' && message.event_id === eventId) {
        console.log('🔄 EventDetail: Estado actualizado', message);
        fetchEventData();
        toast.info(`Estado cambiado a: ${message.new_status}`, {
          description: event?.name || 'Evento actualizado',
          duration: 2000
        });
      } else if (message.type === 'event_costs_update' && message.event_id === eventId) {
        console.log('🔄 EventDetail: Costes actualizados', message);
        fetchEventData();
        toast.info('Costes del evento actualizados', {
          duration: 2000
        });
      }
    });

    return unsubscribe;
  }, [subscribe, fetchEventData, eventId, event]);

  const handleBarcodeSearch = async (e) => {
    e.preventDefault();
    if (!entryFormData.barcode.trim() || scanningProduct) return;
    
    const currentBarcode = entryFormData.barcode.trim();
    setScanningProduct(true);
    
    // Limpiar barcode inmediatamente
    setEntryFormData(prev => ({ ...prev, barcode: '' }));

    try {
      const response = await api.getProductByBarcode(currentBarcode);
      const product = response.data;
      
      if (batchMode) {
        // Esperar un momento antes de procesar para evitar doble submit
        await new Promise(resolve => setTimeout(resolve, 100));
        addToBatchEntry(product);
      } else {
        // Modo individual: mostrar en formulario
        setEntryFormData(prev => ({
          ...prev,
          product_id: product.id,
          product_name: product.name
        }));
        toast.success(`Producto encontrado: ${product.name}`);
      }
    } catch (error) {
      toast.error('Producto no encontrado');
    } finally {
      setScanningProduct(false);
    }
  };

  const addToBatchEntry = (product) => {
    setBatchItems(prevItems => {
      const existingIndex = prevItems.findIndex(item => item.product_id === product.id);
      
      if (existingIndex >= 0) {
        // Producto ya existe: incrementar cantidad en exactamente 1
        const newItems = [...prevItems];
        const newQuantity = newItems[existingIndex].quantity + 1;
        newItems[existingIndex] = {
          ...newItems[existingIndex],
          quantity: newQuantity
        };
        toast.success(`${product.name}: +1 (Total: ${newQuantity})`, { duration: 1500 });
        return newItems;
      } else {
        toast.success(`✓ ${product.name} agregado al lote`, { duration: 1500 });
        return [...prevItems, {
          product_id: product.id,
          barcode: product.barcode,
          name: product.name,
          quantity: 1
        }];
      }
    });
  };

  const updateBatchEntryQuantity = (productId, newQuantity) => {
    if (newQuantity < 1) return;
    setBatchItems(prevItems =>
      prevItems.map(item =>
        item.product_id === productId
          ? { ...item, quantity: parseInt(newQuantity) }
          : item
      )
    );
  };

  const removeBatchEntryItem = (productId) => {
    setBatchItems(prevItems => prevItems.filter(item => item.product_id !== productId));
    toast.info('Producto eliminado del lote');
  };

  const clearBatchEntry = () => {
    setBatchItems([]);
    setEntryFormData({ barcode: '', product_id: '', product_name: '', quantity: '' });
  };

  const confirmBatchEntry = async () => {
    if (batchItems.length === 0) {
      toast.error('El lote está vacío');
      return;
    }

    setConfirmingBatch(true);
    
    try {
      let successCount = 0;
      let errorCount = 0;

      for (const item of batchItems) {
        try {
          await api.createMovement({
            product_id: item.product_id,
            type: 'entry',
            quantity: item.quantity,
            event_id: eventId,
            notes: `Devolución de evento (lote): ${event.name}`
          });
          successCount++;
        } catch (error) {
          console.error(`Error al crear entrada para ${item.name}:`, error);
          errorCount++;
        }
      }

      if (errorCount === 0) {
        toast.success(`✓ Lote confirmado: ${successCount} devoluciones registradas`, {
          duration: 3000
        });
        setBatchItems([]);
        setEntryFormData({ barcode: '', product_id: '', product_name: '', quantity: '' });
        setEntryDialogOpen(false);
        fetchEventData();
      } else {
        toast.warning(`Lote parcial: ${successCount} OK, ${errorCount} errores`, {
          duration: 4000
        });
      }

    } catch (error) {
      toast.error('Error al confirmar lote');
    } finally {
      setConfirmingBatch(false);
    }
  };

  const handleCreateEntry = async (e) => {
    e.preventDefault();
    
    if (!entryFormData.product_id) {
      toast.error('Por favor, escanea un producto primero');
      return;
    }

    try {
      await api.createMovement({
        product_id: entryFormData.product_id,
        type: 'entry',
        quantity: parseInt(entryFormData.quantity),
        event_id: eventId,
        notes: `Devolución de evento: ${event.name}`
      });
      
      toast.success('Entrada registrada correctamente');
      setEntryDialogOpen(false);
      setEntryFormData({ barcode: '', product_id: '', product_name: '', quantity: '' });
      fetchEventData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al crear entrada');
    }
  };

  const handleResetEntry = () => {
    setEntryFormData({ barcode: '', product_id: '', product_name: '', quantity: '' });
  };

  const handleUpdateCosts = async () => {
    try {
      // Convertir strings a números antes de enviar
      const costsToSave = {
        coste_camareros: parseFloat(costFormData.coste_camareros) || 0,
        coste_gastos_variables: parseFloat(costFormData.coste_gastos_variables) || 0,
        coste_transporte: parseFloat(costFormData.coste_transporte) || 0,
        coste_extra1: parseFloat(costFormData.coste_extra1) || 0,
        coste_extra2: parseFloat(costFormData.coste_extra2) || 0,
        income: parseFloat(costFormData.income) || 0
      };
      
      await api.updateCustomer(eventId, costsToSave);
      toast.success('Costes del evento actualizados');
      setEditingCosts(false);
      fetchEventData();
    } catch (error) {
      toast.error('Error al actualizar costes');
    }
  };

  const handleExportExcel = async () => {
    try {
      const response = await api.exportEventToExcel(eventId);
      const safeEventName = event.name.replace(/[^a-zA-Z0-9 _-]/g, '');
      saveAs(response.data, `Evento_${safeEventName}_${eventId.substring(0, 8)}.xlsx`);
      toast.success('Archivo Excel descargado');
    } catch (error) {
      toast.error('Error al exportar a Excel');
    }
  };

  const handleStatusChange = async (newStatus) => {
    try {
      await api.updateEventStatus(eventId, newStatus);
      toast.success(`Estado cambiado a: ${newStatus}`);
      fetchEventData(); // Refrescar datos del evento
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al cambiar estado');
    }
  };

  const getNextStatus = (currentStatus) => {
    const transitions = {
      'Planificación': 'En curso',
      'Planificado': 'En curso',  // Variante sin acento
      'En curso': 'Finalizado',
      'Finalizado': null
    };
    return transitions[currentStatus] || null;
  };

  const getStatusColor = (status) => {
    const colors = {
      'Planificación': 'bg-blue-100 text-blue-700 border-blue-200',
      'Planificado': 'bg-blue-100 text-blue-700 border-blue-200',  // Variante sin acento
      'En curso': 'bg-green-100 text-green-700 border-green-200',
      'Finalizado': 'bg-slate-100 text-slate-700 border-slate-200'
    };
    return colors[status] || 'bg-slate-100 text-slate-700 border-slate-200';
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-slate-600">Cargando...</div>
      </div>
    );
  }

  if (!event) {
    return (
      <div className="text-center py-8">
        <p className="text-slate-600">Evento no encontrado</p>
        <Button onClick={() => navigate('/events')} className="mt-4">
          Volver a Eventos
        </Button>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col gap-4">
        <div className="flex items-center gap-4">
          <Button
            variant="ghost"
            size="icon"
            onClick={() => navigate('/events')}
          >
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <div>
            <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight text-slate-900 dark:text-white">
              {event.name}
            </h1>
            <p className="text-sm sm:text-base text-slate-600 dark:text-slate-300 mt-1">
              {event.address || 'Sin ubicación'}
            </p>
          </div>
        </div>
        <div className="flex gap-2">
          <Button
            variant="default"
            onClick={handleExportExcel}
            className="w-full sm:w-auto"
          >
            <Download className="h-4 w-4 mr-2" />
            Exportar Excel
          </Button>
        </div>
      </div>

      {/* Event Info Card */}
      <Card>
        <CardHeader>
          <CardTitle>Información del Evento</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <p className="text-sm text-slate-500 mb-2">Estado del Evento</p>
              <div className="flex flex-col gap-2">
                <span className={`inline-flex items-center px-3 py-1.5 rounded-lg text-sm font-medium border ${getStatusColor(event.estado || 'Planificación')}`}>
                  {event.estado || 'Planificación'}
                </span>
                {getNextStatus(event.estado || 'Planificación') && (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handleStatusChange(getNextStatus(event.estado || 'Planificación'))}
                    className="w-full"
                  >
                    Cambiar a: {getNextStatus(event.estado || 'Planificación')}
                  </Button>
                )}
                {event.estado === 'Finalizado' && (
                  <p className="text-xs text-slate-500">
                    ✓ Evento finalizado (bloqueado)
                  </p>
                )}
              </div>
            </div>
            <div>
              <p className="text-sm text-slate-500">Fecha Inicio</p>
              <p className="font-medium">{event.fecha_inicio || '-'}</p>
            </div>
            <div>
              <p className="text-sm text-slate-500">Fecha Fin</p>
              <p className="font-medium">{event.fecha_fin || '-'}</p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Entrada de Productos (Devoluciones) */}
      <Card>
        <CardHeader className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <CardTitle className="flex items-center gap-2">
            <TrendingUp className="h-5 w-5 text-green-500" />
            Entrada de Productos (Devoluciones)
          </CardTitle>
          <Dialog open={entryDialogOpen} onOpenChange={setEntryDialogOpen}>
            <DialogTrigger asChild>
              <Button 
                variant="primary" 
                size="sm" 
                className="w-full sm:w-auto"
                disabled={event.estado === 'Finalizado'}
              >
                <Plus className="h-4 w-4 mr-2" />
                {event.estado === 'Finalizado' ? 'Evento Finalizado' : 'Crear Entrada'}
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle>Registrar Entrada/Devolución</DialogTitle>
              </DialogHeader>

              {/* Toggle Modo Lote */}
              <div className="p-3 bg-slate-50 dark:bg-slate-800 rounded-lg">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <span className="text-lg">{batchMode ? '📦' : '📱'}</span>
                    <div>
                      <p className="font-medium text-sm">{batchMode ? 'Modo Lote Activo' : 'Escaneo Individual'}</p>
                      <p className="text-xs text-slate-500">{batchMode ? 'Múltiples productos' : 'Uno por uno'}</p>
                    </div>
                  </div>
                  <button
                    type="button"
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      const newMode = !batchMode;
                      setBatchMode(newMode);
                      if (!newMode) {
                        setBatchItems([]);
                      }
                    }}
                    aria-label={batchMode ? 'Desactivar modo lote' : 'Activar modo lote'}
                    className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors touch-manipulation cursor-pointer ${
                      batchMode ? 'bg-amber-600' : 'bg-slate-300 dark:bg-slate-600'
                    }`}
                  >
                    <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform shadow-md ${
                      batchMode ? 'translate-x-6' : 'translate-x-1'
                    }`} />
                  </button>
                </div>
              </div>
              
              {/* Barcode Scanner Section */}
              {(batchMode || !entryFormData.product_id) ? (
                <form onSubmit={handleBarcodeSearch} className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="barcode">Código de Barras *</Label>
                    <Input
                      id="barcode"
                      type="text"
                      value={entryFormData.barcode}
                      onChange={(e) => setEntryFormData({...entryFormData, barcode: e.target.value})}
                      placeholder="Escanea o escribe el código"
                      className="font-mono"
                      autoFocus
                      required
                    />
                    <p className="text-xs text-slate-500">
                      {batchMode ? 'Escanea múltiples productos consecutivamente' : 'Escanea el código de barras del producto'}
                    </p>
                  </div>
                  <Button 
                    type="submit" 
                    variant="default" 
                    className="w-full"
                    disabled={scanningProduct}
                  >
                    {scanningProduct ? 'Buscando...' : batchMode ? 'Agregar al Lote' : 'Buscar Producto'}
                  </Button>
                </form>
              ) : (
                <form onSubmit={handleCreateEntry} className="space-y-4">
                  {/* Product Found */}
                  <div className="p-4 bg-green-50 border border-green-200 rounded-lg">
                    <p className="text-sm font-semibold text-green-800">✓ Producto encontrado</p>
                    <p className="text-base font-bold text-green-900 mt-1">{entryFormData.product_name}</p>
                    <p className="text-xs text-green-700 font-mono mt-1">Código: {entryFormData.barcode}</p>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="entry_quantity">Cantidad Devuelta *</Label>
                    <Input
                      id="entry_quantity"
                      type="number"
                      inputMode="numeric"
                      min="1"
                      value={entryFormData.quantity}
                      onChange={(e) => setEntryFormData({...entryFormData, quantity: e.target.value})}
                      onFocus={handleNumericInputFocus}
                      onMouseUp={handleNumericInputMouseUp}
                      placeholder="Cantidad devuelta"
                      required
                    />
                  </div>

                  <div className="flex justify-end gap-3 mt-6">
                    <Button 
                      type="button" 
                      variant="secondary" 
                      onClick={handleResetEntry}
                    >
                      Escanear Otro
                    </Button>
                    <Button 
                      type="button" 
                      variant="outline" 
                      onClick={() => {
                        setEntryDialogOpen(false);
                        handleResetEntry();
                      }}
                    >
                      Cancelar
                    </Button>
                    <Button type="submit" variant="success">
                      Registrar Entrada
                    </Button>
                  </div>
                </form>
              )}

              {/* Lista del Lote (solo en modo lote) */}
              {batchMode && batchItems.length > 0 && (
                <div className="mt-4 p-4 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg">
                  <div className="flex items-center justify-between mb-3">
                    <h3 className="font-semibold text-sm">📦 Lote Actual ({batchItems.length} productos)</h3>
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={clearBatchEntry}
                      className="text-xs"
                    >
                      Limpiar
                    </Button>
                  </div>
                  <div className="space-y-2 max-h-48 overflow-y-auto">
                    {batchItems.map((item) => (
                      <div key={item.product_id} className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2 p-2 bg-white dark:bg-slate-800 rounded border">
                        <div className="flex-1 min-w-0">
                          <p className="font-medium text-sm truncate">{item.name}</p>
                          <p className="text-xs text-slate-500 truncate">{item.barcode}</p>
                        </div>
                        <div className="flex items-center gap-2">
                          <Input
                            type="number"
                            inputMode="numeric"
                            min="1"
                            value={item.quantity}
                            onChange={(e) => updateBatchEntryQuantity(item.product_id, e.target.value)}
                            onFocus={handleNumericInputFocus}
                            onMouseUp={handleNumericInputMouseUp}
                            className="flex-1 sm:w-16 h-8 text-center text-sm font-bold"
                          />
                          <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            onClick={() => removeBatchEntryItem(item.product_id)}
                            className="h-8 w-8 p-0 shrink-0"
                          >
                            <X className="h-3 w-3" />
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                  <Button
                    type="button"
                    onClick={confirmBatchEntry}
                    disabled={confirmingBatch || batchItems.length === 0}
                    className="w-full mt-4 bg-amber-600 hover:bg-amber-700"
                  >
                    {confirmingBatch ? (
                      <div className="flex items-center gap-2">
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                        Confirmando...
                      </div>
                    ) : (
                      `Confirmar Lote (${batchItems.length} devoluciones)`
                    )}
                  </Button>
                </div>
              )}
            </DialogContent>
          </Dialog>
        </CardHeader>
        <CardContent>
          <p className="text-sm text-slate-600 dark:text-slate-400 mb-4">
            Registra las devoluciones de productos del evento usando el escáner
          </p>
          <ResponsiveTable
            data={movements.entries}
            columns={[
              { key: 'product_name', label: 'Producto' },
              { key: 'barcode', label: 'Código' },
              { key: 'quantity', label: 'Cantidad' },
              { 
                key: 'created_at', 
                label: 'Fecha',
                render: (item) => new Date(item.created_at).toLocaleString('es-ES', {
                  year: 'numeric',
                  month: 'short',
                  day: 'numeric',
                  hour: '2-digit',
                  minute: '2-digit'
                })
              }
            ]}
            emptyMessage="No hay entradas registradas"
          />
        </CardContent>
      </Card>

      {/* Block C: Costes del Evento */}
      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle className="flex items-center gap-2">
            💰 Costes del Evento
          </CardTitle>
          <Button
            variant={editingCosts ? "secondary" : "outline"}
            size="sm"
            onClick={() => setEditingCosts(!editingCosts)}
          >
            {editingCosts ? 'Cancelar' : 'Editar Costes'}
          </Button>
        </CardHeader>
        <CardContent>
          {editingCosts ? (
            <div className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="coste_camareros">Coste Camareros (€)</Label>
                  <Input
                    id="coste_camareros"
                    type="number"
                    step="0.01"
                    inputMode="decimal"
                    value={costFormData.coste_camareros}
                    onChange={(e) => setCostFormData({...costFormData, coste_camareros: e.target.value})}
                    onFocus={handleNumericInputFocus}
                    onMouseUp={handleNumericInputMouseUp}
                  />
                </div>
                <div>
                  <Label htmlFor="coste_gastos_variables">Gastos Variables (€)</Label>
                  <Input
                    id="coste_gastos_variables"
                    type="number"
                    step="0.01"
                    inputMode="decimal"
                    value={costFormData.coste_gastos_variables}
                    onChange={(e) => setCostFormData({...costFormData, coste_gastos_variables: e.target.value})}
                    onFocus={handleNumericInputFocus}
                    onMouseUp={handleNumericInputMouseUp}
                  />
                </div>
                <div>
                  <Label htmlFor="coste_transporte">Transporte (€)</Label>
                  <Input
                    id="coste_transporte"
                    type="number"
                    step="0.01"
                    inputMode="decimal"
                    value={costFormData.coste_transporte}
                    onChange={(e) => setCostFormData({...costFormData, coste_transporte: e.target.value})}
                    onFocus={handleNumericInputFocus}
                    onMouseUp={handleNumericInputMouseUp}
                  />
                </div>
                <div>
                  <Label htmlFor="coste_extra1">Extra 1 (€)</Label>
                  <Input
                    id="coste_extra1"
                    type="number"
                    step="0.01"
                    inputMode="decimal"
                    value={costFormData.coste_extra1}
                    onChange={(e) => setCostFormData({...costFormData, coste_extra1: e.target.value})}
                    onFocus={handleNumericInputFocus}
                    onMouseUp={handleNumericInputMouseUp}
                  />
                </div>
                <div>
                  <Label htmlFor="coste_extra2">Extra 2 (€)</Label>
                  <Input
                    id="coste_extra2"
                    type="number"
                    step="0.01"
                    inputMode="decimal"
                    value={costFormData.coste_extra2}
                    onChange={(e) => setCostFormData({...costFormData, coste_extra2: e.target.value})}
                    onFocus={handleNumericInputFocus}
                    onMouseUp={handleNumericInputMouseUp}
                  />
                </div>
              </div>
              
              {/* Sección de Ingresos */}
              <div className="mt-4 pt-4 border-t border-slate-200 dark:border-slate-700">
                <h4 className="text-sm font-semibold text-slate-700 dark:text-slate-300 mb-3">💰 Ingresos del Evento</h4>
                <div>
                  <Label htmlFor="income">Ingresos Generados (€)</Label>
                  <Input
                    id="income"
                    type="number"
                    step="0.01"
                    inputMode="decimal"
                    value={costFormData.income}
                    onChange={(e) => setCostFormData({...costFormData, income: e.target.value})}
                    onFocus={handleNumericInputFocus}
                    onMouseUp={handleNumericInputMouseUp}
                    className="border-green-300 dark:border-green-600"
                  />
                </div>
              </div>
              
              <Button onClick={handleUpdateCosts} className="w-full mt-4">
                Guardar Costes e Ingresos
              </Button>
            </div>
          ) : (
            <div className="space-y-4">
              <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                <div className="space-y-1">
                  <p className="text-sm text-slate-500">Camareros</p>
                  <p className="text-lg font-semibold">€{event?.coste_camareros || 0}</p>
                </div>
                <div className="space-y-1">
                  <p className="text-sm text-slate-500">Gastos Variables</p>
                  <p className="text-lg font-semibold">€{event?.coste_gastos_variables || 0}</p>
                </div>
                <div className="space-y-1">
                  <p className="text-sm text-slate-500">Transporte</p>
                  <p className="text-lg font-semibold">€{event?.coste_transporte || 0}</p>
                </div>
                <div className="space-y-1">
                  <p className="text-sm text-slate-500">Extra 1</p>
                  <p className="text-lg font-semibold">€{event?.coste_extra1 || 0}</p>
                </div>
                <div className="space-y-1">
                  <p className="text-sm text-slate-500">Extra 2</p>
                  <p className="text-lg font-semibold">€{event?.coste_extra2 || 0}</p>
                </div>
                <div className="space-y-1">
                  <p className="text-sm text-slate-500 font-bold">Total Costes</p>
                  <p className="text-xl font-bold text-orange-600">€{totals?.costes_evento?.total || 0}</p>
                </div>
              </div>
              
              {/* Mostrar Ingresos */}
              <div className="pt-4 border-t border-slate-200 dark:border-slate-700">
                <div className="bg-green-50 dark:bg-green-900/20 p-4 rounded-lg">
                  <p className="text-sm text-slate-500 dark:text-slate-400 mb-1">💰 Ingresos del Evento</p>
                  <p className="text-2xl font-bold text-green-600 dark:text-green-400">€{event?.income || 0}</p>
                </div>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Block D: Summary with Costs */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <BarChart3 className="h-5 w-5 text-indigo-500" />
            Resumen de Ventas y Costes
          </CardTitle>
        </CardHeader>
        <CardContent>
          {/* Totals Summary */}
          {totals && (
            <div className="mb-6 p-4 bg-slate-50 dark:bg-slate-800 rounded-lg">
              <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-4">
                <div className="space-y-1">
                  <p className="text-sm text-slate-500">Total Vendido</p>
                  <p className="text-2xl font-bold">{totals.total_vendido_unidades} uds</p>
                </div>
                <div className="space-y-1">
                  <p className="text-sm text-slate-500">Ingresos Totales</p>
                  <p className="text-2xl font-bold text-green-600">€{totals.ingresos}</p>
                </div>
                <div className="space-y-1">
                  <p className="text-sm text-slate-500">Total Costes</p>
                  <p className="text-2xl font-bold text-orange-600">€{totals.costes_evento?.total || 0}</p>
                </div>
                <div className="space-y-1">
                  <p className="text-sm text-slate-500">Beneficio Bruto</p>
                  <p className={`text-2xl font-bold ${totals.beneficio_bruto >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                    €{totals.beneficio_bruto}
                  </p>
                </div>
                <div className="space-y-1">
                  <p className="text-sm text-slate-500 font-bold">Beneficio Neto</p>
                  <p className={`text-3xl font-bold ${totals.beneficio_neto >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                    €{totals.beneficio_neto}
                  </p>
                </div>
              </div>
            </div>
          )}
          
          <ResponsiveTable
            data={summary}
            columns={[
              { key: 'product_name', label: 'Producto', className: 'font-medium' },
              { key: 'barcode', label: 'Código' },
              { 
                key: 'enviado', 
                label: 'Enviado', 
                className: 'text-right',
                cellClassName: 'text-right'
              },
              { 
                key: 'devuelto', 
                label: 'Devuelto', 
                className: 'text-right',
                cellClassName: 'text-right'
              },
              { 
                key: 'vendido', 
                label: 'Vendido', 
                className: 'text-right font-bold',
                cellClassName: 'text-right font-bold',
                render: (item) => (
                  <span className="text-green-600 dark:text-green-400 font-bold">
                    {item.vendido}
                  </span>
                )
              },
              { 
                key: 'coste_medio', 
                label: 'Coste Medio', 
                className: 'text-right',
                cellClassName: 'text-right',
                render: (item) => `€${item.coste_medio?.toFixed(2) || '0.00'}`
              },
              { 
                key: 'coste_total_producto', 
                label: 'Coste Total', 
                className: 'text-right font-bold',
                cellClassName: 'text-right font-bold',
                render: (item) => (
                  <span className="text-red-600 dark:text-red-400 font-bold">
                    €{item.coste_total_producto?.toFixed(2) || '0.00'}
                  </span>
                )
              }
            ]}
            emptyMessage="No hay movimientos para este evento aún"
          />
        </CardContent>
      </Card>
    </div>
  );
};
